import React from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { ThingMapPin } from '@fs/zion-icon'
import { baseUrl } from '@fs/zion-config'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import HeaderIconButton from '../HeaderIconButton/HeaderIconButton'

function getFhcLink({ external, usefhcLocationLocalizedLink, usefhcLocationNewLink, useLocationsPath }) {
  const externalUrl = external ? baseUrl : ''
  if (usefhcLocationLocalizedLink) {
    const languages = {
      en: '/en/centers/about',
      lv: '/lv/centers/par-familysearch-centriem', // Latvian
      zh: '/zh/centers/%E9%97%9C%E6%96%BC-familysearch-%E4%B8%AD%E5%BF%83', // Chinese:(Taiwan)
      fr: '/fr/centers/a-propos-des-centres-familysearch', // French
      sm: '/sm/centers/e-uiga-i-nofoaga-autu-o-le-familysearch', // Samoan
      uk: '/uk/centers/%D0%BF%D1%80%D0%BE-%D1%86%D0%B5%D0%BD%D1%82%D1%80%D0%B8-familysearch', // Ukrainian
      id: '/id/centers/tentang-pusat-familysearch', // Indonesian
      pl: '/pl/centers/informacje-o-centrach-familysearch', // Polish
      nl: '/nl/centers/over-familysearch-centra', // Dutch
      de: '/de/centers/naheres-zu-den-familysearch-centern', // German
      ro: '/ro/centers/despre-centrele-familysearch', // Romanian
      ja: '/ja/centers/%E3%83%95%E3%82%A1%E3%83%9F%E3%83%AA%E3%83%BC%E3%82%B5%E3%83%BC%E3%83%81%E3%82%BB%E3%83%B3%E3%82%BF%E3%83%BC%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6', // Japanese
      es: '/es/centers/acerca-de-los-centros-familysearch', // Spanish
      no: '/no/centers/om-familysearch-sentre', // Norwegian
      'zh-hans': '/zh-hans/centers/%E5%85%B3%E4%BA%8E-familysearch-%E4%B8%AD%E5%BF%83', // Chinese (China)
      ko: '/ko/centers/%ED%8C%A8%EB%B0%80%EB%A6%AC%EC%84%9C%EC%B9%98-%EC%84%BC%ED%84%B0-%EC%86%8C%EA%B0%9C', // Korean
      bg: '/bg/centers/%D0%BE%D1%82%D0%BD%D0%BE%D1%81%D0%BD%D0%BE-%D1%86%D0%B5%D0%BD%D1%82%D1%80%D0%BE%D0%B2%D0%B5%D1%82%D0%B5-%D0%BD%D0%B0-familysearch', // Bulgarian
      tl: '/tl/centers/tungkol-sa-mga-familysearch-center', // Tagalog
      et: '/et/centers/info-pereajalookeskuste-kohta', // Estonian
      hu: '/hu/centers/a-familysearch-kozpontokrol', // Hungarian
      vi: '/vi/centers/ve-cac-trung-tam-familysearch', // Vietnamese
      pt: '/pt/centers/sobre-os-centros-de-historia-da-familia', // Portuguese
      hr: '/hr/centers/o-familysearch-centrima', // Croatian
      ru: '/ru/centers/%D0%BE-%D1%86%D0%B5%D0%BD%D1%82%D1%80%D0%B0%D1%85-familysearch', // Russian
      da: '/da/centers/om-familysearch-centre', // Danish
      sk: '/sk/centers/o-centrach-familysearch', // Slovak
      th: '/th/centers/%E0%B9%80%E0%B8%81-%E0%B8%A2%E0%B8%A7%E0%B8%81-%E0%B8%9A%E0%B8%A8-%E0%B8%99%E0%B8%A2-familysearch', // Thai
      ceb: '/ceb/centers/kabahin-sa-mga-sentro-sa-familysearch', // Cebuano
      sv: '/sv/centers/om-familysearch-center', // Swedish
      to: '/to/centers/fekau%CA%BBaki-mo-e-ngaahi-senita-familysearch', // Tongan
      cs: '/cs/centers/o-strediscich-familysearch', // Czech
      fi: '/fi/centers/tietoa-familysearch-keskuksista', // Finnish
      mg: '/mg/centers/momba-ireo-ivon-toeranny-familysearch', // Malagasy
      sq: '/sq/centers/rreth-qendrave-te-familysearch-it', // Albanian
      ms: '/ms/centers/tentang-pusat', // Malay
      km: '/km/centers/%E1%9E%A2-%E1%9E%96-%E1%9E%98%E1%9E%87-%E1%9E%88%E1%9E%98%E1%9E%8E-%E1%9E%8C%E1%9E%9B-%E1%9E%80%E1%9E%98-%E1%9E%98%E1%9E%9C-%E1%9E%92-%E1%9E%9F-%E1%9E%9A-%E1%9E%9C%E1%9E%87-%E1%9E%9A-%E1%9E%9C-%E1%9E%80-%E1%9E%9A-%E1%9E%98%E1%9E%82-%E1%9E%9A-%E1%9E%9F-%E1%9E%9A', // Khmer
      fj: '/fj/centers/veika-me-baleta-na-vei-valenivolavola-ni-familysearch', // Fijian
      it: '/it/centers/informazioni-sui-centri-familysearch', // Italian
      mn: '/mn/centers/familysearch-%D1%82%D3%A9%D0%B2%D2%AF%D2%AF%D0%B4%D0%B8%D0%B8%D0%BD-%D1%82%D1%83%D1%85%D0%B0%D0%B8', // Mongolian
    }
    return `${externalUrl}${languages[i18n.language] || languages.en}`
  }
  if (useLocationsPath) {
    return `${externalUrl}/${i18n.language}/locations/about`
  }
  if (usefhcLocationNewLink) {
    return `${externalUrl}/${i18n.language}/centers/about`
  }
  return `${externalUrl}/centers/locations/`
}

export default function LocationIconButton({ linkName, external }) {
  const { isOn: usefhcLocationNewLink } = useFeatureFlag('home_header_fhcLocationNewLink')
  const { isOn: usefhcLocationLocalizedLink } = useFeatureFlag('home_header_fhcLocationLocalizedLink')
  const { isOn: useLocationsPath } = useFeatureFlag('help_locations_path')
  const { t } = useTranslation()

  return (
    <HeaderIconButton
      Icon={ThingMapPin}
      title={t('header.locations', 'Locations')}
      to={getFhcLink({ external, usefhcLocationLocalizedLink, usefhcLocationNewLink, useLocationsPath })}
      linkName={linkName}
      external
      data-test-id="hdr_FS_Center_Locations"
    />
  )
}
